/**
 * The controller for "Ask the profile".
 *
 * It sits above `PublicProfileView` because the surface is split across two
 * places in that tree — the widget under the deck, and the deck-end card inside
 * the deck frame — and both drive the same conversation. Everything here is
 * eager: no file reachable from this one may import `@assistant-ui/*`, or the
 * runtime lands in the main bundle and the split point in
 * `ask-profile-section.tsx` buys nothing.
 *
 * The one piece of machinery worth reading closely is the sender handshake. The
 * composer is mounted before the thread chunk has downloaded, so a question can
 * be asked before anything exists to run it. `ask()` therefore queues, and the
 * thread drains the queue when it registers. A cold first click is correct, not
 * dropped.
 */

import {
  createContext,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { trackEvent as trackGaEvent } from '../../../lib/gtag'
import type { PresetQuestionKey } from '../config/preset-questions'
import { type ProfileChatHistoryTurn, reconstructQuestionText } from '../lib/chat-history'
import { resolveCitations } from '../lib/citations'
import { visibleAnswerText } from '../lib/follow-ups'
import {
  appendLocalChatHistoryTurn,
  clearLocalChatHistory,
  readLocalChatHistory,
} from '../lib/local-chat-history'
import { isEvaluationQuestion } from '../lib/question-intent'
import type {
  ChatRunState,
  ChatSource,
  ChatStatusState,
  ProfileChatOpener,
} from '../types/chat.types'

/** How the question was asked. Preset and slide questions are free (§6 quota). */
export type AskProfileEntry = 'chip' | 'typed' | 'slide' | 'followup'

export interface AskProfileTurnInput {
  entry: AskProfileEntry
  /**
   * What the investor sees in their own bubble, and what the follow-up filter
   * dedupes against. For a preset or a slide it is NOT the prompt the model
   * receives — that body is server-canonical, keyed off `presetKey` /
   * `slideNumber`.
   */
  text: string
  presetKey?: PresetQuestionKey
  /** Captured at click time, so paging the deck mid-stream cannot rewrite it. */
  slideNumber?: number
}

export interface AskProfileTurn extends AskProfileTurnInput {
  /**
   * The idempotency key. Stamped once, here, so a turn that was queued while
   * the thread chunk downloaded and a turn that is re-sent both address the
   * same server-side row rather than paying for the question twice.
   */
  clientTurnId: string
  /**
   * The chat this question belongs to, as the reader sees it. Rotated by
   * "Clear chat" and fresh on every page load, so it changes exactly when the
   * pane empties — which is what scopes the model's history and groups the
   * admin transcript into the chats the reader actually had.
   */
  chatSessionId: string
}

export type AskProfileDoor = 'agent_follow' | 'thesis_fit'

/** The six measured placements (§8). Every door event carries one. */
export type AskProfileDoorPlacement =
  | 'cta_bar'
  | 'deck_end'
  | 'question_limit'
  | 'evaluation_question'
  | 'after_connect'
  | 'pass_path'
  // The post-gate card at the foot of the profile. Doc §9: "'Follow this
  // company' replaces 'Get custom diligence'" — the button that currently sends
  // an anonymous investor to an "Investor access only" wall.
  | 'post_gate'

export interface AskProfileDoorSubmission {
  door: AskProfileDoor
  placement: AskProfileDoorPlacement
  email: string
  /** The agent door's free-text brief. Never leaves the visitor's own record. */
  brief?: string
  /** The thesis door's four fields, only those actually chosen. */
  thesis?: Record<string, string>
}

interface OpenDoor {
  door: AskProfileDoor
  placement: AskProfileDoorPlacement
}

/**
 * Who is reading.
 *
 * `founder` is the owner previewing their own profile. Every investor
 * conversion surface is withheld from them — the two doors, "Ask the founder
 * directly", the book-a-call card — because they are the founder: those CTAs
 * are addressed to someone else, and a founder clicking "Have your agent
 * follow it" on their own company is nonsense that would also pollute the
 * signal the doors exist to measure.
 */
export type AskProfileAudience = 'investor' | 'founder'

export interface AskProfileContextValue {
  audience: AskProfileAudience
  /** How a question reaches the server. See ProfileChatAdapterOptions. */
  openStream: ProfileChatOpener
  startupName: string
  /** Prefills the door's email field when this device has already given one. */
  initialEmail: string
  /** What the model actually received, as the server named it. */
  sources: readonly ChatSource[]
  /** Slides the model was given, for the deck row's count. */
  slideCount: number
  /** Documents on this link besides the deck, read or not, for the documents row. */
  documentCount: number
  /** True once the thread holds at least one message: hides the chips. */
  paneHasContent: boolean
  isRunning: boolean
  /** Drives the reading pips — one `reading_source` frame per source. */
  status: ChatStatusState | null
  sourcesRead: readonly string[]
  /** Epoch ms the running turn started, for the elapsed-seconds counter. */
  runStartedAt: number | null
  /** Live re-attestation state for the turn in flight; see chat-run-state.ts. */
  citationsPending: readonly number[]
  citationVerdicts: Readonly<Record<number, boolean>>
  /**
   * Sources the turn IN FLIGHT has actually received, off its own `sources`
   * frame — not `sources` above, which is the whole profile's connected-source
   * checklist (every document on file, whether or not this turn's context
   * included it). The footer's "N sources read" must count what THIS answer
   * was actually given, the same number the finished answer's own `data`
   * reports, or the count visibly changes the moment the run completes.
   */
  runSources: readonly ChatSource[]
  askedQuestions: readonly string[]
  /**
   * Questions asked while another was running, in the order they will be sent.
   * They are shown immediately: a question that vanishes on Enter and
   * reappears a minute later reads as a dropped message, not a queued one.
   */
  pendingQuestions: readonly string[]
  openDoorState: OpenDoor | null
  /**
   * Prior turns of the chat session on screen, fetched once on mount when this
   * device is returning to a session `localStorage` remembers. Null before the
   * fetch resolves or when there is nothing to restore — the thread reads it
   * exactly once, for `initialMessages`, so it does not need to be reactive.
   */
  history: { chatSessionId: string; turns: readonly ProfileChatHistoryTurn[] } | null
  composerRef: RefObject<HTMLElement | null>
  ask: (turn: AskProfileTurnInput) => void
  registerSender: (sender: ((turn: AskProfileTurn) => void) | null) => void
  /** The thread registers how to empty itself; the eager shell calls it. */
  registerClearer: (clear: (() => void) | null) => void
  /**
   * The composer registers how to focus its own field. `ask()` calls it after
   * every question — typed, chip, slide or follow-up — so the field is ready
   * for the next one without the reader having to click back into it. A
   * button click moves focus to the button first; this runs after, in the
   * same synchronous handler, so the field wins with no visible flicker.
   */
  registerFocusComposer: (focus: (() => void) | null) => void
  clearChat: () => void
  reportRunStart: () => void
  reportRunProgress: (state: ChatRunState) => void
  reportRunEnd: (turn: AskProfileTurn, state: ChatRunState) => void
  openDoor: (door: AskProfileDoor, placement: AskProfileDoorPlacement) => void
  closeDoor: () => void
  submitDoor: (submission: AskProfileDoorSubmission) => void
  /** Routes into the existing "Connect with founder" path on the profile. */
  connectFounder: () => void
  trackEvent: (type: string, resourceId?: string, metadata?: Record<string, unknown>) => void
}

/**
 * What is safe to store: the sentinel and any partial prefix of it withheld,
 * and registry ids resolved to their server-written labels — the same
 * resolution `chat-adapter.ts` applies before rendering, duplicated here
 * rather than imported so this eager file never pulls in that lazy chunk's
 * `@assistant-ui/*`-typed module.
 */
function localAnswerText(state: ChatRunState): string {
  return resolveCitations(visibleAnswerText(state.text), state.sources).text
}

const LOCAL_HISTORY_KIND_BY_ENTRY: Record<AskProfileEntry, ProfileChatHistoryTurn['kind']> = {
  chip: 'PRESET',
  slide: 'SLIDE',
  followup: 'FOLLOWUP',
  typed: 'TYPED',
}

const AskProfileContext = createContext<AskProfileContextValue | null>(null)

export function useAskProfile(): AskProfileContextValue {
  const context = useContext(AskProfileContext)
  if (!context) throw new Error('useAskProfile must be used within AskProfileProvider')
  return context
}

export interface AskProfileProviderProps {
  audience?: AskProfileAudience
  openStream: ProfileChatOpener
  startupName: string
  initialEmail?: string
  sources: readonly ChatSource[]
  slideCount: number
  documentCount?: number
  onDoorSubmit: (submission: AskProfileDoorSubmission) => void
  onConnectFounder: () => void
  onTrackEvent: (type: string, resourceId?: string, metadata?: Record<string, unknown>) => void
  /**
   * What scopes the persisted session id in `localStorage` — the share token.
   * Reload survival is opt-in per instance: omitted (as the founder preview
   * does) the chat session is fresh in memory on every mount exactly as it
   * always was, matching that surface's "remembers nothing" design.
   */
  historyKey?: string
  /** Reads prior turns of one chat session back, for `historyKey`'s reload. */
  fetchHistory?: (chatSessionId: string) => Promise<ProfileChatHistoryTurn[]>
  /**
   * The founder preview's own reload survival — mutually exclusive with
   * `historyKey`/`fetchHistory`. There is no server row for that route to
   * read back (see `local-chat-history.ts`), so the whole turn is written to
   * this browser's `localStorage` the moment it finishes, keyed by this.
   */
  localHistoryKey?: string
  children: ReactNode
}

export function AskProfileProvider({
  audience = 'investor',
  openStream,
  startupName,
  initialEmail = '',
  sources,
  slideCount,
  documentCount = 0,
  onDoorSubmit,
  onConnectFounder,
  onTrackEvent,
  historyKey,
  fetchHistory,
  localHistoryKey,
  children,
}: AskProfileProviderProps) {
  const [paneHasContent, setPaneHasContent] = useState(false)
  const [isRunning, setIsRunning] = useState(false)
  const [status, setStatus] = useState<ChatStatusState | null>(null)
  const [sourcesRead, setSourcesRead] = useState<readonly string[]>([])
  const [runStartedAt, setRunStartedAt] = useState<number | null>(null)
  const [citationsPending, setCitationsPending] = useState<readonly number[]>([])
  const [citationVerdicts, setCitationVerdicts] = useState<Readonly<Record<number, boolean>>>({})
  const [runSources, setRunSources] = useState<readonly ChatSource[]>([])
  const [askedQuestions, setAskedQuestions] = useState<readonly string[]>([])
  const [pendingQuestions, setPendingQuestions] = useState<readonly string[]>([])
  const [openDoorState, setOpenDoorState] = useState<OpenDoor | null>(null)
  const [history, setHistory] = useState<{
    chatSessionId: string
    turns: readonly ProfileChatHistoryTurn[]
  } | null>(null)

  // Undefined (not persisted at all, as the founder preview passes it) reads
  // identically to a key that has never been written: `ask()` mints a fresh
  // session id every mount either way.
  const storageKey = historyKey ? `ask-profile-chat-session:${historyKey}` : null

  const composerRef = useRef<HTMLElement | null>(null)
  const senderRef = useRef<((turn: AskProfileTurn) => void) | null>(null)
  const clearerRef = useRef<(() => void) | null>(null)
  const focusComposerRef = useRef<(() => void) | null>(null)
  const queueRef = useRef<AskProfileTurn[]>([])
  // Mirrors of state that the analytics calls read. They are refs because a
  // callback that closed over the state values would have to be rebuilt on
  // every question, and the adapter holds one of them for the life of a run.
  const askCountRef = useRef(0)
  // `ask` must see the live value; closing over the state would make it
  // decide with whatever was true when the callback was last built.
  const isRunningRef = useRef(false)
  const runStartedAtRef = useRef<number | null>(null)

  // There is deliberately no scroll-into-view here any more. The pane now
  // sizes to its content up to a small ceiling (`max-h-[62dvh] md:max-h-[600px]`
  // in `ask-profile-section.tsx`) instead of opening at a fixed height, so a
  // first question no longer needs the page moved under it to stay on screen
  // — it already is. Two prior attempts at this (composer-anchoring, then
  // section-top-anchoring with a `ResizeObserver` growth correction) were
  // both working around a pane that could open taller than the viewport;
  // once that stopped being true, the scroll became a page nudging the
  // reader for no reason.

  const ask = useCallback(
    (input: AskProfileTurnInput) => {
      if (chatSessionIdRef.current === null) {
        chatSessionIdRef.current = crypto.randomUUID()
        // Best-effort: a private window or blocked storage means the id is
        // simply not there to find on the next reload, same as today.
        if (storageKey) {
          try {
            localStorage.setItem(storageKey, chatSessionIdRef.current)
          } catch {
            // Ignored — see above.
          }
        }
      }
      const turn: AskProfileTurn = {
        ...input,
        clientTurnId: crypto.randomUUID(),
        chatSessionId: chatSessionIdRef.current,
      }
      // Google Analytics sees ordinals and closed vocabularies only: with a
      // known deck, a slide number identifies the company as surely as its name,
      // and the meter promises the founder never sees what you ask.
      if (!paneHasContent && turn.entry !== 'followup') {
        trackGaEvent('ask_profile_opened', { entry: turn.entry })
      }
      trackGaEvent('ask_profile_question_sent', {
        entry: turn.entry,
        preset: turn.presetKey,
        question_index: askCountRef.current,
      })
      askCountRef.current += 1
      setPaneHasContent(true)
      setAskedQuestions((previous) => [...previous, turn.text])
      const sender = senderRef.current
      // Queued in two cases, drained the same way.
      //
      // 1. No runtime yet — the chunk is still downloading.
      // 2. A turn is already running. Appending to the runtime mid-answer
      //    CANCELS the running one, and because the answer is emitted in a
      //    single frame after verification, cancelling loses all of it: the
      //    reader gets an empty bubble where their first question was. So a
      //    second question waits its turn rather than destroying the first.
      if (sender === null || isRunningRef.current) {
        queueRef.current.push(turn)
        setPendingQuestions((previous) => [...previous, turn.text])
      } else {
        sender(turn)
      }
      // Last, so a chip or send-button click's own focus (set by the browser
      // before this handler runs) is overridden rather than raced.
      focusComposerRef.current?.()
    },
    [paneHasContent, storageKey]
  )

  const registerFocusComposer = useCallback((focus: (() => void) | null) => {
    focusComposerRef.current = focus
  }, [])

  const registerSender = useCallback((sender: ((turn: AskProfileTurn) => void) | null) => {
    senderRef.current = sender
    if (sender === null) return
    const queued = queueRef.current
    queueRef.current = []
    // Only the FIRST is sent. The rest stay queued and drain one per finished
    // run, because appending a second turn now would cancel the first.
    const [first, ...rest] = queued
    queueRef.current = rest
    // The pending list must lose exactly what was just sent. This drain is the
    // cold-start one — the thread chunk had not loaded when the question was
    // asked — and forgetting it here left the very first question of every
    // session listed as queued forever, beside its own answer.
    if (first) {
      setPendingQuestions((previous) => previous.slice(1))
      sender(first)
    }
  }, [])

  // A ref, not state: nothing renders from it, and a question queued during
  // the clear must read the id current at the moment it was asked. Created on
  // the first question rather than as the ref's initial value, which React
  // would evaluate — and throw away — on every render of this provider.
  const chatSessionIdRef = useRef<string | null>(null)

  // A returning device finds its own chat session id waiting in storage and
  // asks the backend for that session's turns — a reload otherwise opens the
  // pane exactly as empty as a brand new visitor's, wiping whatever the
  // investor was reading. Runs once, on mount: `ask()` mints a fresh id (and
  // this effect has nothing to restore) the first time a device is ever seen.
  // `storageKey`/`fetchHistory` are stable for the life of this provider
  // (derived from the share token), so re-running on every render would only
  // re-fetch a session that never changed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only intent
  useEffect(() => {
    // The founder preview: everything it needs is already sitting in
    // `localStorage`, written by `reportRunEnd` below — no fetch, no
    // network round trip, nothing that can fail asynchronously.
    if (localHistoryKey) {
      const local = readLocalChatHistory(localHistoryKey)
      if (local && local.turns.length > 0) {
        chatSessionIdRef.current = local.chatSessionId
        setHistory(local)
        setPaneHasContent(true)
        setAskedQuestions(local.turns.map((turn) => reconstructQuestionText(turn)))
      }
      return
    }
    if (!storageKey || !fetchHistory) return
    let stored: string | null = null
    try {
      stored = localStorage.getItem(storageKey)
    } catch {
      return
    }
    if (!stored) return
    chatSessionIdRef.current = stored
    let cancelled = false
    fetchHistory(stored)
      .then((turns) => {
        if (cancelled || turns.length === 0) return
        setHistory({ chatSessionId: stored, turns })
        setPaneHasContent(true)
        setAskedQuestions(turns.map((turn) => reconstructQuestionText(turn)))
      })
      .catch(() => {
        // Safe by construction: a failed read starts the pane exactly as
        // empty as it would be for a first-time visitor, never broken.
      })
    return () => {
      cancelled = true
    }
  }, [])

  const registerClearer = useCallback((clear: (() => void) | null) => {
    clearerRef.current = clear
  }, [])

  /**
   * Empty the pane and put the chips back.
   *
   * CLIENT-SIDE ONLY, deliberately. The turn ledger is the corpus this feature
   * exists to build, so a reader clearing their own view must not delete what
   * they asked — and the founder's counts must not move either. This resets
   * what is on screen and nothing else.
   */
  const clearChat = useCallback(() => {
    // A new chat, for the model as well as the screen: the next question is
    // answered without the cleared ones as history. Its id is minted by that
    // next question.
    chatSessionIdRef.current = null
    if (storageKey) {
      try {
        localStorage.removeItem(storageKey)
      } catch {
        // Ignored, matching the write side.
      }
    }
    if (localHistoryKey) clearLocalChatHistory(localHistoryKey)
    clearerRef.current?.()
    queueRef.current = []
    askCountRef.current = 0
    isRunningRef.current = false
    setPendingQuestions([])
    setPaneHasContent(false)
    setAskedQuestions([])
    setIsRunning(false)
    setStatus(null)
    setSourcesRead([])
    setRunStartedAt(null)
    setCitationsPending([])
    setCitationVerdicts({})
    setRunSources([])
    setHistory(null)
  }, [storageKey, localHistoryKey])

  const reportRunStart = useCallback(() => {
    const startedAt = Date.now()
    runStartedAtRef.current = startedAt
    isRunningRef.current = true
    setIsRunning(true)
    setStatus(null)
    setSourcesRead([])
    setRunStartedAt(startedAt)
    setCitationsPending([])
    setCitationVerdicts({})
    setRunSources([])
  }, [])

  const reportRunProgress = useCallback((state: ChatRunState) => {
    setStatus(state.status)
    setSourcesRead(state.sourcesRead)
    setCitationsPending(state.citationsPending)
    setCitationVerdicts(state.citationVerdicts)
    setRunSources(state.sources)
  }, [])

  const openDoor = useCallback(
    (door: AskProfileDoor, placement: AskProfileDoorPlacement) => {
      setOpenDoorState({ door, placement })
      onTrackEvent('ask_profile_door_open', door, { placement })
      trackGaEvent('ask_profile_door_opened', { door, placement })
    },
    [onTrackEvent]
  )

  const reportRunEnd = useCallback(
    (turn: AskProfileTurn, state: ChatRunState) => {
      const startedAt = runStartedAtRef.current
      runStartedAtRef.current = null
      isRunningRef.current = false
      setIsRunning(false)
      setStatus(null)
      setRunStartedAt(null)

      if (state.phase === 'error') {
        // The adapter stamps `network` on anything that never reached the
        // server; everything else arrived as an `error` frame, which by
        // definition came from one.
        trackGaEvent('ask_profile_answer_failed', {
          entry: turn.entry,
          reason: state.error?.code === 'network' ? 'network' : 'server',
        })
      } else {
        trackGaEvent('ask_profile_answer_completed', {
          entry: turn.entry,
          duration_seconds: startedAt === null ? 0 : Math.round((Date.now() - startedAt) / 1000),
          truncated: state.phase === 'truncated',
        })
      }

      // The founder preview's own persistence: a completed turn is written
      // here, the instant it finishes — never partway through a stream, and
      // never a `truncated` or `error` run, matching what the investor path's
      // server-side ledger only ever keeps as COMPLETE.
      if (localHistoryKey && state.phase === 'done' && chatSessionIdRef.current) {
        appendLocalChatHistoryTurn(localHistoryKey, chatSessionIdRef.current, {
          kind: LOCAL_HISTORY_KIND_BY_ENTRY[turn.entry],
          presetKey: turn.presetKey ?? null,
          slideNumber: turn.slideNumber ?? null,
          questionText: turn.text,
          answerText: localAnswerText(state),
          sources: [...state.sources],
          followUps: [...state.followUps],
        })
      }

      if (shouldOpenThesisDoor(turn, state)) openDoor('thesis_fit', 'evaluation_question')

      // Whatever was asked while this one ran. Drained here rather than in the
      // thread's register effect, because that only fires on mount.
      const queued = queueRef.current.shift()
      if (queued) {
        setPendingQuestions((previous) => previous.slice(1))
        senderRef.current?.(queued)
      }
    },
    [openDoor, localHistoryKey]
  )

  const closeDoor = useCallback(() => setOpenDoorState(null), [])

  const submitDoor = useCallback(
    (submission: AskProfileDoorSubmission) => {
      onDoorSubmit(submission)
      // The brief and the thesis values themselves are deliberately absent: the
      // founder reads these events, and the promise is that they never see what
      // the investor asked for.
      onTrackEvent('ask_profile_door_submit', submission.door, {
        placement: submission.placement,
        hadEmail: initialEmail !== '',
        hasBrief: submission.brief !== undefined ? submission.brief.trim() !== '' : undefined,
        thesisFieldsSet:
          submission.thesis !== undefined ? Object.keys(submission.thesis).length : undefined,
      })
      trackGaEvent('ask_profile_door_submitted', {
        door: submission.door,
        had_email: initialEmail !== '',
      })
    },
    [initialEmail, onDoorSubmit, onTrackEvent]
  )

  const value = useMemo<AskProfileContextValue>(
    () => ({
      audience,
      openStream,
      startupName,
      initialEmail,
      sources,
      slideCount,
      documentCount,
      paneHasContent,
      isRunning,
      status,
      sourcesRead,
      runStartedAt,
      citationsPending,
      citationVerdicts,
      runSources,
      askedQuestions,
      pendingQuestions,
      openDoorState,
      history,
      composerRef,
      ask,
      registerSender,
      registerClearer,
      registerFocusComposer,
      clearChat,
      reportRunStart,
      reportRunProgress,
      reportRunEnd,
      openDoor,
      closeDoor,
      submitDoor,
      connectFounder: onConnectFounder,
      trackEvent: onTrackEvent,
    }),
    [
      audience,
      openStream,
      startupName,
      initialEmail,
      sources,
      slideCount,
      documentCount,
      paneHasContent,
      isRunning,
      status,
      sourcesRead,
      runStartedAt,
      citationsPending,
      citationVerdicts,
      runSources,
      askedQuestions,
      pendingQuestions,
      openDoorState,
      history,
      ask,
      registerSender,
      registerClearer,
      registerFocusComposer,
      clearChat,
      reportRunStart,
      reportRunProgress,
      reportRunEnd,
      openDoor,
      closeDoor,
      submitDoor,
      onConnectFounder,
      onTrackEvent,
    ]
  )

  return <AskProfileContext.Provider value={value}>{children}</AskProfileContext.Provider>
}

/**
 * Whether this answer should open the thesis door.
 *
 * The server ran the same classification and returned its verdict on `done`;
 * that verdict wins, because two classifiers disagreeing produce either a
 * refusal with no door or a door under a purely factual answer. The client's
 * own classifier is the fallback for the one case the frame cannot cover — a
 * stream that ended without `done`.
 */
export function shouldOpenThesisDoor(turn: AskProfileTurn, state: ChatRunState): boolean {
  if (turn.entry !== 'typed') return false
  if (state.phase === 'done') return state.refusal === 'evaluative'
  if (state.phase === 'error') return false
  return isEvaluationQuestion(turn.text)
}
